import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import EmptyState from '../ui/EmptyState'
import Loader from '../shared/Loader'
import Pagination from '../ui/pagination'
import ProgressionService from '../../services/api/ProgressionService'
import SectionDrawer from './SectionDrawer'
import SectionGradeCard from './SectionGradeCard'
import usePaginatedGrid from './hooks/usePaginatedGrid'

const PER_PAGE_OPTIONS = [
  { value: 14, label: '14' },
  { value: 28, label: '28' },
  { value: 42, label: '42' },
  { value: 56, label: '56' }
]
const DEFAULT_PER_PAGE = 14

const EMPTY_MESSAGE = 'Le détail par section apparaîtra ici une fois les données disponibles.'

const LOADING_OVERLAY_CLASSES = 'tw:absolute tw:inset-0 tw:flex tw:items-center '
  + 'tw:justify-center tw:bg-white/70 tw:z-10'

const SectionGradesGrid = ({
  collectId, dateRange, gradeMax, mode, granularity,
  operatorId, resourceIds, resourcesHref, resourceSearch, variableId
}) => {
  const [selectedSection, setSelectedSection] = useState(null)

  const fetcher = useCallback(({ page, perPage }) => ProgressionService.fetchList(
    collectId, null, {
      name: 'section_grades',
      page,
      perPage,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      operatorId: operatorId || null,
      resourceIds,
      resourceSearch: resourceSearch || null,
      variableId: variableId || null
    }
  ), [collectId, dateRange, operatorId, resourceIds, resourceSearch, variableId])

  const filtersKey = JSON.stringify([
    collectId,
    dateRange.startDate,
    dateRange.endDate,
    operatorId,
    resourceIds,
    resourceSearch,
    variableId
  ])

  const { rows, meta, page, perPage, isLoading, setPage, setPerPage } = usePaginatedGrid({
    fetcher, rowsKey: 'rows', defaultPerPage: DEFAULT_PER_PAGE, filtersKey
  })

  // Spin only when reloading; the chart's loader carries the first-paint signal.
  const isReloading = isLoading && rows.length > 0

  const handleCloseDrawer = useCallback(() => setSelectedSection(null), [])

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:relative">
        {rows.length === 0 && !isLoading? <EmptyState message={EMPTY_MESSAGE} />: (
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
            {rows.map(row => (
              <SectionGradeCard
                key={row.section_id}
                grade={row.grade}
                gradeMax={gradeMax}
                hasGradeQuestions={row.has_grade_questions}
                id={row.section_id}
                mode={mode}
                name={row.name}
                satisfaction={row.satisfaction}
                onSelect={setSelectedSection}
              />
            ))}
          </div>
        )}
        {isReloading ? <div className={LOADING_OVERLAY_CLASSES}><Loader /></div> : null}
      </div>
      {meta.count > 0 && (
        <Pagination
          disabled={isLoading}
          lastPage={meta.pages}
          page={page}
          perPage={perPage}
          perPageOptions={PER_PAGE_OPTIONS}
          total={meta.count}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
      <SectionDrawer
        collectId={collectId}
        dateRange={dateRange}
        gradeMax={gradeMax}
        granularity={granularity}
        isOpen={Boolean(selectedSection)}
        mode={mode}
        operatorId={operatorId}
        resourceIds={resourceIds}
        resourcesHref={resourcesHref}
        resourceSearch={resourceSearch}
        section={selectedSection}
        variableId={variableId}
        onClose={handleCloseDrawer}
      />
    </div>
  )
}

SectionGradesGrid.propTypes = {
  collectId: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string
  }).isRequired,
  gradeMax: PropTypes.number.isRequired,
  granularity: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['grade', 'satisfaction']).isRequired,
  operatorId: PropTypes.string,
  resourceIds: PropTypes.arrayOf(PropTypes.number),
  resourceSearch: PropTypes.string,
  resourcesHref: PropTypes.string,
  variableId: PropTypes.string
}

SectionGradesGrid.defaultProps = {
  operatorId: '',
  resourceIds: [],
  resourceSearch: '',
  resourcesHref: '#',
  variableId: ''
}

export default SectionGradesGrid
